import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlayCircle, Pencil, Trash2 } from 'lucide-react';
import { useI18n } from '@/i18n';
import { 
  Button, 
  SearchInput, 
  Select,
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell,
  TableEmpty,
  StatusBadge,
  Icon,
  Spinner,
  type StatusType,
} from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { useAgents, useDeleteAgent } from '@/features/agents/hooks/useAgents';
import { AgentTestModal } from '@/components/agents';
import type { Agent } from '@/features/agents/types';

// Map agent status to StatusType
const mapStatus = (status: string): StatusType => {
  switch (status) {
    case 'active': return 'active';
    case 'draft': return 'draft';
    case 'deprecated': return 'deprecated';
    case 'inactive': return 'draft'; // Map inactive to draft for UI
    case 'archived': return 'deprecated'; // Map archived to deprecated for UI
    default: return 'default';
  }
};

// =============================================================================
// Agents List Page
// =============================================================================

export function AgentsListPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [testAgent, setTestAgent] = useState<Agent | null>(null);

  // ISS-580: búsqueda en servidor (debounced) + paginación. Reset a la
  // página 1 cuando cambian los filtros.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  const {
    data: agents = [],
    isLoading,
    error,
    total,
    totalPages,
  } = useAgents({
    search: debouncedSearch || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    page,
    pageSize: 20,
  });
  const deleteMutation = useDeleteAgent();

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleDelete = async (agent: Agent) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      t('admin.agents.deleteConfirm', { name: agent.name }) ||
        `Delete agent "${agent.name}"? This cannot be undone.`,
    );
    if (!ok) return;
    try {
      await deleteMutation.mutateAsync(agent.id);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e instanceof Error ? e.message : 'Failed to delete agent');
    }
  };

  // El filtrado (búsqueda + estado) ya lo aplica el servidor (ISS-580);
  // aquí solo se renderiza la página recibida.
  const visibleAgents = agents;

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300">
            {t('common.error')}: {error instanceof Error ? error.message : 'Failed to load agents'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="agents-page">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.agents.title')}</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">{t('admin.agents.subtitle')}</p>
        </div>
        <Button leftIcon="plus" onClick={() => navigate('/agents/new')} data-testid="create-agent-button">
          {t('admin.agents.newAgent')}
        </Button>
      </div>

      {/* El uso/límite del plan ya no se muestra aquí: los planes no limitan
          el consumo (los boosters se facturan según pricing). «Mi licencia»
          sigue mostrando el consumo real para transparencia (ISS-803). */}

      {/* Filters */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row">
        <div className="flex-1">
          <SearchInput
            placeholder={t('admin.agents.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
          />
        </div>
        <Select
          options={[
            { value: 'all', label: t('admin.agents.filterByStatus') },
            { value: 'active', label: t('admin.agents.status.active') },
            { value: 'draft', label: t('admin.agents.status.draft') },
          ]}
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="w-full sm:w-48"
        />
      </div>

      {/* Agents Table */}
      <Table data-testid="agent-list">
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.agents.table.agent')}</TableHead>
            <TableHead>{t('admin.agents.table.status')}</TableHead>
            <TableHead>{t('admin.agents.table.version')}</TableHead>
            <TableHead className="w-16"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleAgents.length === 0 ? (
            <TableEmpty 
              colSpan={4} 
              message={t('admin.agents.empty')}
              action={
                <Button variant="outline" leftIcon="plus" onClick={() => navigate('/agents/new')}>
                  {t('admin.agents.newAgent')}
                </Button>
              }
            />
          ) : (
            visibleAgents.map((agent) => (
              <TableRow key={agent.id}>
                <TableCell>
                  <Link to={`/agents/${agent.id}`} className="flex items-center gap-3 group">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                      <Icon name="bot" size={20} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {agent.name}
                      </span>
                      {agent.delivery_mode === 'use_only' && (
                        <span
                          data-testid="platform-agent-badge"
                          className="ml-2 inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300"
                        >
                          {t('admin.agents.platformManaged.badge') || 'Plataforma'}
                        </span>
                      )}
                      {agent.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {agent.description}
                        </p>
                      )}
                    </div>
                  </Link>
                </TableCell>
                <TableCell>
                  <StatusBadge status={mapStatus(agent.status)} label={t(`admin.agents.status.${agent.status}`)} />
                </TableCell>
                <TableCell className="text-gray-600 dark:text-gray-400">1.0.0</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTestAgent(agent)}
                      className="flex items-center gap-1"
                    >
                      <PlayCircle className="h-4 w-4" />
                      {t('admin.agents.test') || 'Test'}
                    </Button>
                    {agent.delivery_mode !== 'use_only' && (
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          aria-label={t('common.actions')}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <Icon name="more" size={18} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => navigate(`/agents/${agent.id}`)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            {t('common.edit') || 'Edit'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() => handleDelete(agent)}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t('common.delete') || 'Delete'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Pagination (ISS-580: el servidor pagina; sin esto los ranks >20
          eran invisibles e inencontrables) */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {t('admin.agents.pagination.showing', {
              shown: String(visibleAgents.length),
              total: String(total),
            }) || `Showing ${visibleAgents.length} of ${total} agents`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              {t('admin.agents.pagination.previous') || 'Previous'}
            </Button>
            <span className="text-sm">
              {t('admin.agents.pagination.pageOf', {
                page: String(page),
                pages: String(totalPages),
              }) || `Page ${page} of ${totalPages}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              {t('admin.agents.pagination.next') || 'Next'}
            </Button>
          </div>
        </div>
      )}

      {/* Test Agent Modal */}
      <AgentTestModal
        open={!!testAgent}
        onOpenChange={(open) => !open && setTestAgent(null)}
        agent={testAgent}
      />
    </div>
  );
}
